import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoCloudUploadOutline,
  IoTrashOutline,
  IoRefreshOutline,
  IoDocumentTextOutline,
  IoPencilOutline,
  IoCheckmarkCircle,
  IoAlertCircle,
  IoTimeOutline,
} from "react-icons/io5";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/auth";
import { AppHeader } from "@/components/AppHeader";
import { BottomNav } from "@/components/BottomNav";
import { tapLight, tapSuccess, tapError } from "@/utils/haptics";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return ""; }
};

const fmtSize = (bytes) => {
  if (!bytes || bytes <= 0) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

const StatusBadge = ({ status }) => {
  const map = {
    queued:  { bg: "#f3f4f6", fg: "#374151", label: "In coda", Icon: IoTimeOutline },
    running: { bg: "#fef3c7", fg: "#92400e", label: "In elaborazione…", Icon: IoRefreshOutline, spin: true },
    done:    { bg: "#dcfce7", fg: "#166534", label: "Attivo", Icon: IoCheckmarkCircle },
    error:   { bg: "#fee2e2", fg: "#991b1b", label: "Errore", Icon: IoAlertCircle },
  };
  const it = map[status] || map.queued;
  const Icon = it.Icon;
  return (
    <span
      data-testid={`listino-status-${status}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: it.bg, color: it.fg,
        fontSize: 11, fontWeight: 700,
        padding: "3px 8px", borderRadius: 999,
      }}
    >
      <Icon size={12} className={it.spin ? "tf-spin" : ""} />
      {it.label}
    </span>
  );
};

/**
 * Titolare-only page to manage multiple Conad price lists (listini).
 * Shows all uploaded listini with metadata and lets the Titolare add, rename or
 * delete a specific listino. Deleting a listino also wipes all its `barcode_cache`
 * entries (option `a`: total cleanup for that listino only).
 */
export default function ListiniManagerPage() {
  const navigate = useNavigate();
  const { isTitolare } = useAuth();
  const [listini, setListini] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const pollRef = useRef(null);
  const importInputRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/listini");
      setListini(Array.isArray(data?.items) ? data.items : []);
      setTotalEntries(data?.total_entries || 0);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Impossibile caricare i listini");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-poll while any listino is still running/queued so the entries_count and
  // status stay fresh without user action.
  useEffect(() => {
    const hasPending = listini.some((l) => l.status === "running" || l.status === "queued");
    if (!hasPending) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = setInterval(() => load(true), 3000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [listini, load]);

  const handleImportPdf = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seleziona un file PDF");
      return;
    }
    // Ask for a display name so the Titolare can distinguish multiple listini.
    const suggested = file.name.replace(/\.pdf$/i, "").slice(0, 60);
    const name = window.prompt("Nome del listino (es. 'Ortofrutta gennaio 2026')", suggested);
    if (name === null) return; // user cancelled
    setImporting(true);
    const uploadingToast = toast.loading("Caricamento PDF…");
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      const url = `/admin/import-listino${name.trim() ? `?name=${encodeURIComponent(name.trim())}` : ""}`;
      await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      tapSuccess();
      toast.dismiss(uploadingToast);
      toast.success("Listino inviato. Elaborazione in corso…");
      load(true);
    } catch (err) {
      tapError();
      toast.dismiss(uploadingToast);
      toast.error(err?.response?.data?.detail || "Errore avvio importazione");
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (item) => {
    const ok = window.confirm(
      `Eliminare definitivamente il listino "${item.name}"?\n\nVerranno rimossi anche tutti i ${item.entries_count || 0} barcode associati.`
    );
    if (!ok) return;
    setDeletingId(item.id);
    try {
      const { data } = await api.delete(`/admin/listini/${item.id}`);
      tapSuccess();
      toast.success(`Listino eliminato · ${data?.deleted_entries || 0} barcode rimossi`);
      setListini((arr) => arr.filter((l) => l.id !== item.id));
      setTotalEntries((n) => Math.max(0, n - (data?.deleted_entries || 0)));
    } catch (e) {
      tapError();
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  const startRename = (item) => {
    setRenamingId(item.id);
    setRenameValue(item.name || "");
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  const commitRename = async (item) => {
    const name = renameValue.trim();
    if (!name) { toast.error("Il nome non può essere vuoto"); return; }
    if (name === item.name) { cancelRename(); return; }
    try {
      await api.patch(`/admin/listini/${item.id}`, { name });
      tapLight();
      setListini((arr) => arr.map((l) => l.id === item.id ? { ...l, name } : l));
      cancelRename();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rinomina");
    }
  };

  if (!isTitolare) {
    // Route already protected, but keep a safety fallback.
    return (
      <div className="mobile-frame">
        <AppHeader title="Gestore Listini" showBack backTo="/" />
        <div style={{ padding: 24, textAlign: "center", color: "var(--tf-muted)" }}>
          Accesso riservato al Titolare
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-frame with-bottom-bar" data-testid="listini-manager-page">
      <AppHeader title="Gestore Listini Conad" showBack backTo="/" />

      <div className="tf-container">
        {/* Summary card */}
        <div
          data-testid="listini-summary"
          style={{
            marginTop: 12,
            padding: "16px 18px",
            borderRadius: 16,
            background: "#0a0a0a",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 12, opacity: 0.7, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>
              Totale prodotti attivi
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, marginTop: 4 }}>
              {totalEntries.toLocaleString("it-IT")}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 12, opacity: 0.7, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>
              Listini
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, marginTop: 4 }}>
              {listini.length}
            </div>
          </div>
        </div>

        {/* Upload button */}
        <input
          ref={importInputRef}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: "none" }}
          data-testid="listino-upload-input"
          onChange={handleImportPdf}
        />
        <button
          type="button"
          data-testid="listino-upload-btn"
          disabled={importing}
          onClick={() => importInputRef.current?.click()}
          style={{
            marginTop: 14,
            width: "100%",
            background: importing ? "#4a4a4a" : "#E2001A",
            color: "#fff",
            border: "none",
            borderRadius: 12,
            padding: "14px 16px",
            fontWeight: 800,
            fontSize: 15,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: importing ? "not-allowed" : "pointer",
          }}
        >
          <IoCloudUploadOutline size={20} />
          {importing ? "Caricamento…" : "Aggiungi un nuovo listino"}
        </button>

        {/* Info */}
        <div className="tf-small" style={{ color: "var(--tf-muted)", marginTop: 8, textAlign: "center" }}>
          I listini restano attivi in parallelo. Elimina un singolo listino per
          togliere solo i suoi barcode dalla cache.
        </div>

        <div className="tf-h2" style={{ marginTop: 20, fontSize: 16 }}>Listini caricati</div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <IoRefreshOutline size={22} className="tf-spin" />
          </div>
        ) : listini.length === 0 ? (
          <div
            data-testid="listini-empty"
            className="tf-small"
            style={{ color: "var(--tf-muted)", padding: 24, textAlign: "center" }}
          >
            Nessun listino caricato. Carica il primo PDF del listino Conad per iniziare.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }} data-testid="listini-list">
            {listini.map((item) => {
              const isRenaming = renamingId === item.id;
              return (
                <div
                  key={item.id}
                  data-testid={`listino-row-${item.id}`}
                  style={{
                    padding: 14,
                    borderRadius: 14,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div
                      style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: "#0a0a0a", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <IoDocumentTextOutline size={22} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isRenaming ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          <input
                            type="text"
                            data-testid={`listino-rename-input-${item.id}`}
                            value={renameValue}
                            autoFocus
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitRename(item);
                              if (e.key === "Escape") cancelRename();
                            }}
                            maxLength={120}
                            style={{
                              flex: 1,
                              padding: "6px 10px",
                              border: "1.5px solid #0a0a0a",
                              borderRadius: 8,
                              fontSize: 14,
                              fontWeight: 700,
                              minWidth: 0,
                            }}
                          />
                          <button
                            type="button"
                            data-testid={`listino-rename-save-${item.id}`}
                            onClick={() => commitRename(item)}
                            style={{
                              padding: "6px 10px", background: "#16a34a", color: "#fff",
                              border: "none", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer",
                            }}
                          >Salva</button>
                          <button
                            type="button"
                            onClick={cancelRename}
                            style={{
                              padding: "6px 10px", background: "transparent", color: "#0a0a0a",
                              border: "1px solid #d1d5db", borderRadius: 8, fontSize: 12, cursor: "pointer",
                            }}
                          >Annulla</button>
                        </div>
                      ) : (
                        <div
                          style={{
                            fontWeight: 700, fontSize: 15,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }}
                        >
                          {item.name}
                        </div>
                      )}
                      <div className="tf-small" style={{ color: "var(--tf-muted)", marginTop: 2 }}>
                        {fmtDate(item.created_at)}
                        {item.filename ? ` · ${item.filename}` : ""}
                        {item.size ? ` · ${fmtSize(item.size)}` : ""}
                      </div>
                    </div>
                    {!isRenaming && (
                      <button
                        type="button"
                        data-testid={`listino-rename-btn-${item.id}`}
                        aria-label="Rinomina"
                        onClick={() => startRename(item)}
                        style={{
                          padding: 6, background: "transparent", border: "none",
                          color: "#6b7280", cursor: "pointer",
                        }}
                      >
                        <IoPencilOutline size={18} />
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`listino-delete-btn-${item.id}`}
                      aria-label="Elimina"
                      disabled={deletingId === item.id}
                      onClick={() => handleDelete(item)}
                      style={{
                        padding: 6, background: "transparent", border: "none",
                        color: "#dc2626", cursor: deletingId === item.id ? "not-allowed" : "pointer",
                        opacity: deletingId === item.id ? 0.5 : 1,
                      }}
                    >
                      {deletingId === item.id ? <IoRefreshOutline className="tf-spin" size={18} /> : <IoTrashOutline size={18} />}
                    </button>
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                    <StatusBadge status={item.status || "queued"} />
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      <strong style={{ color: "#0a0a0a" }}>
                        {(item.entries_count || 0).toLocaleString("it-IT")}
                      </strong>
                      {" "}prodotti
                      {item.status === "running" && item.total ? ` / ${item.total.toLocaleString("it-IT")}` : ""}
                    </div>
                    {item.status === "error" && item.error && (
                      <div style={{ fontSize: 11, color: "#dc2626", flexBasis: "100%" }}>
                        {item.error}
                      </div>
                    )}
                  </div>

                  {/* Progress bar while running */}
                  {item.status === "running" && item.total > 0 && (
                    <div style={{ position: "relative", height: 4, borderRadius: 2, background: "#e5e7eb", overflow: "hidden" }}>
                      <div
                        style={{
                          position: "absolute", top: 0, bottom: 0, left: 0,
                          width: `${Math.min(100, ((item.entries_count || 0) / item.total) * 100)}%`,
                          background: "#E2001A",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
